/*
 * Session — the one thing a UI asks "may I show this control, and if not, what do
 * I tell the person".
 *
 * The pieces already existed and had to be assembled by hand at every call site:
 * `identityFrom` turns a session into an Identity, `evaluate` composes the
 * two planes, `whyDisabled` renders the sentence. Nothing held them together, so
 * every component that wanted to grey out a button had to know about the IdP, the
 * entitlements stream, and the evaluation order — which is how four UIs end up
 * with four subtly different answers to the same question.
 *
 * This holds identity and plan, notifies on change, and answers. It is the client
 * half of the same decision `authz.Evaluate` makes on the server, in the same
 * order, so the tooltip before the click and the error after it agree.
 *
 * LOAD-BEARING, and worth repeating because it is the thing people get wrong: this
 * is UX only. The real "no" is the server refusing the call. A UI that hides a
 * button has made the product nicer; a UI that believes hiding the button is the
 * protection has made it insecure. Everything here may be lied to by anyone with
 * dev tools, and none of it matters, because the gateway decides again.
 *
 * FAIL-OPEN, deliberately, and the asymmetry with the server is the design. Before
 * the plan arrives, capability checks pass. A control that briefly appears and
 * then errors costs one clear message; a control hidden because a stream had not
 * connected yet tells a paying customer they own nothing, every time their
 * connection blips. The server is fail-closed precisely so this can afford not to
 * be.
 */
import { ANON, identityFrom, setGrantMatrixProvider } from './identity';
import { canFrom, isSuperAdmin } from './authz';
import { evaluate, whyDisabled, hasCapability, meetsLevel, requirementFor, } from './entitlements';
/** Fired whenever identity or plan changes. Components re-read and re-render. */
export const SESSION_CHANGED = 'session-changed';
/**
 * The live session. One per app, created at boot and fed from two sources that
 * arrive independently and in either order:
 *
 *   - identity, from the IdP session (synchronously available on the main thread)
 *   - plan, from SessionEntitlements.Subscribe (a stream, so it lands later)
 *
 * Neither blocks the other. A UI renders with identity alone and tightens when the
 * plan arrives, which is exactly the fail-open behaviour above.
 */
export class Session extends EventTarget {
    #identity = ANON;
    #plan;
    constructor() {
        super();
    }
    get identity() {
        return this.#identity;
    }
    get plan() {
        return this.#plan;
    }
    /** True once the plan has arrived. Useful for deciding whether to show an
     *  upgrade prompt, which would be wrong to show before anything is known. */
    get planKnown() {
        return this.#plan !== undefined;
    }
    get isSuperAdmin() {
        return isSuperAdmin(this.#identity);
    }
    /**
     * May the user call this RPC — permission, capability and level, in one answer.
     *
     * The boolean form of what `<vitesse-gated>` renders, for code that cannot use an
     * element: the symbol grid is vanilla DOM with pooled rows, so it needs to decide
     * whether to show a button, not to wrap one.
     *
     * Reads the requirement off the RPC's own annotation, so a caller never restates
     * a permission. An UNANNOTATED method is REFUSED, matching the server: the
     * annotation is where a decision gets recorded, so its absence is an unanswered
     * question rather than a permissive answer.
     */
    allowsRpc(method) {
        const req = requirementFor(method);
        // FAIL CLOSED, matching the server. This used to allow an unannotated method
        // on the reasoning that the server did not gate it either — true at the time,
        // and the reason a correctly-written <vitesse-gated> could render fully open
        // while looking right in review. The server now refuses an unannotated
        // procedure, so allowing one here would offer a control that is certain to be
        // rejected, which is worse than hiding it.
        return req ? this.allows(req) : false;
    }
    /** Why an RPC is refused, or undefined when it is allowed. The sentence the
     *  server would give, so a tooltip and an error cannot disagree. */
    whyRpc(method) {
        const req = requirementFor(method);
        return req ? this.why(req) : undefined;
    }
    /** Re-read identity from the host. Safe to call on every listener tick — it
     *  only notifies when something actually changed, so a token refresh does not
     *  re-render the app. */
    syncFromHost(host) {
        this.#setIdentity(identityFrom(host));
    }
    /** For tests and for hosts that resolve identity themselves. */
    setIdentity(id) {
        this.#setIdentity(id);
    }
    /** Feed a frame from SessionEntitlements.Subscribe. */
    setPlan(plan) {
        if (samePlan(this.#plan, plan))
            return;
        this.#plan = plan;
        this.dispatchEvent(new Event(SESSION_CHANGED));
    }
    #setIdentity(id) {
        if (sameIdentity(this.#identity, id))
            return;
        this.#identity = id;
        this.dispatchEvent(new Event(SESSION_CHANGED));
    }
    /** May the caller perform this permission? Identity only — use `allows` when
     *  the plan matters too. */
    can(permission) {
        return canFrom(this.#identity, permission);
    }
    /** Does the org's plan include this capability? Fail-open before the plan lands. */
    has(feature) {
        return hasCapability(this.#plan, feature);
    }
    /** The full decision — both planes, same composition as the server.
     *
     *  `usage` feeds the quantitative layer: how many of `req.consumes` are in use
     *  right now, from whatever the caller counts (a roster, a list). Omitted, the
     *  layer is skipped — a count nobody supplied is not zero. */
    denials(req, usage) {
        return evaluate(this.#identity, req, this.#plan, usage);
    }
    allows(req, usage) {
        return this.denials(req, usage).length === 0;
    }
    /** The sentence to put in a tooltip; undefined when the action is allowed.
     *  Permission first, because that is the refusal the person can act on
     *  themselves — "ask your admin" and "upgrade your plan" send them to
     *  completely different places. */
    why(req, usage) {
        return whyDisabled(this.#identity, req, this.#plan, usage);
    }
    /**
     * The capability's fully resolved TYPED value — level, set, ceilings and all.
     *
     * The one reader for everything a boolean cannot express. A UI renders depth
     * from `choice` and filters from `members`; it never infers either from the
     * DATA, which is what makes withheld and genuinely-absent distinguishable.
     *
     * Returns undefined when the plan has not arrived — deliberately distinct from
     * a capability that is present and inactive.
     */
    value(feature) {
        return this.#plan?.capabilities.find((c) => c.feature === feature);
    }
    /**
     * Does a graduated capability reach `level`? Fail-open before the plan lands,
     * matching `has` — an unknown plan must not read as "you own nothing".
     */
    atLeast(feature, level) {
        if (!this.#plan)
            return true;
        const v = this.value(feature);
        if (!v?.active)
            return false;
        return meetsLevel(feature, v.choice ?? '', level);
    }
    /** Is `member` in a set capability, e.g. an event kind this org receives? */
    includes(feature, member) {
        if (!this.#plan)
            return true; // fail-open, as above
        const v = this.value(feature);
        return !!v?.active && !!v.members?.includes(member);
    }
    /**
     * A resolved ceiling, or undefined for UNLIMITED.
     *
     * Undefined must never be read as zero: they are opposite answers, and a UI
     * showing "0 days of history" to a customer on the unlimited tier is the worst
     * available direction for that mistake.
     */
    limit(feature, key) {
        return this.value(feature)?.limits?.[key];
    }
    /** Subscribe to changes. Returns the unsubscribe, so a component's disconnect
     *  is one call and cannot forget which listener it added. */
    onChange(fn) {
        this.addEventListener(SESSION_CHANGED, fn);
        return () => this.removeEventListener(SESSION_CHANGED, fn);
    }
}
/** Identity equality — compared by value because `identityFrom` builds a fresh
 *  object on every session tick, and reference equality would notify constantly. */
function sameIdentity(a, b) {
    if (a.userId !== b.userId || a.orgId !== b.orgId || a.role !== b.role || a.orgName !== b.orgName)
        return false;
    if (a.permissions.size !== b.permissions.size)
        return false;
    for (const p of a.permissions)
        if (!b.permissions.has(p))
            return false;
    return sameGrants(a.grants, b.grants);
}
function sameGrants(a, b) {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    const ak = Object.keys(a);
    if (ak.length !== Object.keys(b).length)
        return false;
    // Compared by VALUE, and an absent key is not the same as a false one: absent
    // means "the role decides" and false means "revoked". Treating them alike would
    // stop the UI noticing a revocation.
    for (const k of ak) {
        if (!(k in b) || a[k] !== b[k])
            return false;
    }
    return true;
}
/** Plan equality, ignoring `graceUntilMs`.
 *
 *  The stream re-sends on a timer as well as on change, and a grace deadline
 *  ticking down would otherwise re-render the whole app every poll while nothing
 *  a user can see has changed. What matters for rendering is which capabilities
 *  are active and what their limits are. */
function samePlan(a, b) {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    if (a.orgId !== b.orgId || a.gatewayId !== b.gatewayId)
        return false;
    const ac = a.capabilities ?? [];
    const bc = b.capabilities ?? [];
    if (ac.length !== bc.length)
        return false;
    for (let i = 0; i < ac.length; i++) {
        const x = ac[i];
        const y = bc[i];
        if (x.feature !== y.feature || x.active !== y.active || x.lifecycle !== y.lifecycle)
            return false;
        // The typed value is part of what changed, and forgetting it here is invisible:
        // an org upgraded from telemetry "basic" to "flows" has the same feature, the
        // same active flag and the same lifecycle, so without this the new plan is
        // discarded as identical and the UI never unlocks what was just paid for.
        if (x.choice !== y.choice)
            return false;
        const xm = x.members ?? [];
        const ym = y.members ?? [];
        if (xm.length !== ym.length)
            return false;
        for (let k = 0; k < xm.length; k++)
            if (xm[k] !== ym[k])
                return false;
        const xl = x.limits ?? {};
        const yl = y.limits ?? {};
        const xk = Object.keys(xl);
        if (xk.length !== Object.keys(yl).length)
            return false;
        for (const k of xk)
            if (xl[k] !== yl[k])
                return false;
    }
    return true;
}
/*
 * The ONE session, held here rather than exported per app.
 *
 * Every app would otherwise repeat the same three lines — construct the Session,
 * sync it from the identity host, re-sync on every tick — and then hand it to each gated
 * component as a property. Both halves are worth removing:
 *
 *   - Four apps wiring identity themselves is four chances to wire it differently,
 *     and the one that forgets `addListener` looks correct until a user switches
 *     org and the UI keeps rendering the old one's permissions.
 *   - A component that must be HANDED a session can only be used where somebody
 *     remembered to hand it one. That failure is SILENT: <vitesse-gated> fails open,
 *     so a forgotten property renders the control ungated — the exact case the
 *     component exists to prevent, and it looks like it is working.
 *
 * So a component asks for the session instead of receiving it, and cannot be
 * dropped into a view in a state where it has none.
 */
let current;
/**
 * Create the app's session and wire identity to the IdP. Call ONCE at boot, before
 * anything renders.
 *
 * `host` is optional so an app can init early and bind identity once the IdP has
 * loaded; passing it does the sync and subscribes to changes. Calling this again
 * REPLACES the session — app code does so once, tests do so per case.
 */
export function initSession(host) {
    current = new Session();
    if (host)
        bindIdentity(host);
    return current;
}
/**
 * Wire an already-created session to the identity host: read identity now, and
 * again on every change. The re-read is what makes an org switch or a sign-out
 * land in the UI; Session only notifies when something actually changed, so a
 * token refresh does not re-render the app.
 */
export function bindIdentity(host) {
    if (!current)
        return;
    const s = current;
    s.syncFromHost(host);
    host.addListener?.(() => s.syncFromHost(host));
}
/*
 * The authored permission matrix, as it arrives on SessionEntitlements.Subscribe.
 *
 * This is the provider identity.ts's TODO(grant-matrix) waited for. The matrix is
 * written by an admin in the org panel, enforced by the api-gateway from its own
 * cache, and — since it rides the same per-caller stream as the plan — pushed to
 * the browser from that same cache. So the lock a control renders and the refusal
 * the gate issues derive from one fact, which is the whole point.
 *
 * Kept by ORG, because that is what the frame is scoped by and what `canFrom`
 * keys on; an org switch re-binds identity and the provider then answers
 * "nothing authored" for the new org until its own frame lands.
 */
let authoredFor = '';
let authored;
const fromStream = (_userId, orgId) => (orgId === authoredFor ? authored : undefined);
/**
 * Feed one frame's authored matrix. Call ONLY when the frame carried the field:
 * an ABSENT field means the store could not say, and the right move is to keep
 * the last picture — the caller must not translate absence into `{}`, which reads
 * as "nothing authored" and hands back every permission an admin revoked.
 *
 * Re-derives identity so every <vitesse-gated> re-renders on the same tick, and
 * installs the value behind the provider so a later IdP tick (a token refresh)
 * re-deriving identity from scratch gets the same answer. The provider is
 * (re)installed on EVERY frame, not once at module load: the install is a
 * module-level global that anything may overwrite, and a stream that stopped
 * being read would fail exactly like the bug this closes — silently, open.
 */
export function setAuthoredGrants(orgId, grants) {
    authoredFor = orgId;
    authored = grants;
    setGrantMatrixProvider(fromStream);
    if (!current || current.identity.orgId !== orgId)
        return;
    current.setIdentity({ ...current.identity, grants });
}
/**
 * The app's session, or undefined before boot.
 *
 * Undefined is a legitimate answer and callers must treat it as FAIL-OPEN, matching
 * Session itself: an unbooted session is a wiring bug, and hiding a paying
 * customer's controls because of one is the worse of the two failures.
 */
export function getSession() {
    return current;
}
//# sourceMappingURL=session.js.map